import { useState } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { PeriodPills } from '@/components/shared/period-pills'

const PERIODS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'all', label: 'Todo período' },
] as const

export type PeriodValue = (typeof PERIODS)[number]['value']
export const DEFAULT_PERIOD: PeriodValue = '30d'

interface CreatorsAnalyticsFiltersProps {
  activePeriod: PeriodValue | undefined
  selectedTier: string
  selectedProgram: string
  dateRange: DateRange | undefined
  hasActiveFilters: boolean
  tiers: { id: string; name: string }[] | undefined
  programs: { id: string; name: string }[] | undefined
  onPeriodChange: (period: PeriodValue) => void
  onTierChange: (val: string) => void
  onProgramChange: (val: string) => void
  onDateChange: (range: DateRange | undefined) => void
  onClearFilters: () => void
}

export function CreatorsAnalyticsFilters({
  activePeriod,
  selectedTier,
  selectedProgram,
  dateRange,
  hasActiveFilters,
  tiers,
  programs,
  onPeriodChange,
  onTierChange,
  onProgramChange,
  onDateChange,
  onClearFilters,
}: CreatorsAnalyticsFiltersProps) {
  const [sheetOpen, setSheetOpen] = useState(false)

  const activeFilterCount = [
    selectedTier !== 'all',
    selectedProgram !== 'all',
    dateRange !== undefined,
  ].filter(Boolean).length

  return (
    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-1.5">
      {/* Period selector — always visible */}
      <PeriodPills options={PERIODS} value={activePeriod} onChange={onPeriodChange} />

      {/* Desktop-only filters */}
      <div className="hidden sm:flex items-center gap-1.5">
        <Select value={selectedTier} onValueChange={onTierChange}>
          <SelectTrigger size="sm" className="w-40 shadow-recessed text-[13px]">
            <SelectValue placeholder="Todos os Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Tiers</SelectItem>
            {tiers?.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedProgram} onValueChange={onProgramChange}>
          <SelectTrigger size="sm" className="w-44 shadow-recessed text-[13px]">
            <SelectValue placeholder="Todos os Programas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Programas</SelectItem>
            {programs?.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DatePickerWithRange
          date={dateRange}
          setDate={onDateChange}
          align="end"
          className="[&_button#date]:sm:w-auto [&_button#date]:h-8 [&_button#date]:shadow-recessed [&_button#date]:text-[13px]"
        />
      </div>

      {/* Mobile filter trigger — opens bottom Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="sm:hidden shadow-recessed">
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
            Filtros
            {activeFilterCount > 0 && (
              <Badge variant="default" className="ml-1.5 h-4 min-w-4 px-1 text-[10px]">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-xl">
          <SheetHeader>
            <SheetTitle>Filtros</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium">Tier</label>
                <Select value={selectedTier} onValueChange={onTierChange}>
                  <SelectTrigger className="shadow-recessed">
                    <SelectValue placeholder="Todos os Tiers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Tiers</SelectItem>
                    {tiers?.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium">Programa</label>
                <Select value={selectedProgram} onValueChange={onProgramChange}>
                  <SelectTrigger className="shadow-recessed">
                    <SelectValue placeholder="Todos os Programas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Programas</SelectItem>
                    {programs?.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium">Período personalizado</label>
                <DatePickerWithRange date={dateRange} setDate={onDateChange} />
              </div>
            </div>
          </SheetBody>
          <SheetFooter className="flex-row">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={() => {
                  onClearFilters()
                  setSheetOpen(false)
                }}
                className="flex-1"
              >
                Limpar filtros
              </Button>
            )}
            <ChonkyButton className="flex-1" onClick={() => setSheetOpen(false)}>
              Aplicar
            </ChonkyButton>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Clear filters — always last */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150"
        >
          <X className="h-3 w-3" />
          Limpar filtros
        </button>
      )}
    </div>
  )
}
